// src/components/OutreachPanel.js
// Gmail outreach pipeline UI: stage candidates → Claude drafts → review/edit/approve
// → connect Gmail → send (guarded) → track by status → sync/triage replies → follow-ups.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Send, Check, Pencil, RefreshCw, UserPlus, Trash2, ArrowLeft,
  ShieldAlert, Inbox, Clock, AlertTriangle, Users, Download, Ban, X as XIcon, Zap
} from 'lucide-react';
import { UserAuth } from '../context/AuthContext';
import {
  draftOutreach, listOutreach, getStats, updateOutreach,
  createGmailDraft, sendOutreach, followUp, syncReplies, fetchShortlist,
  listSuppression, addSuppression, removeSuppression,
  backgroundStatus, backgroundAuthUrl, updateBackgroundSettings, disconnectBackground, queueApproved, queueOutreach,
  saveBackgroundOAuthConfig, runBackgroundNow
} from '../utils/outreach-api';
import { connectGmail, isGmailConnected, ensureGmailToken, clearGmailToken } from '../utils/gmail-connect';

const STATUS_TABS = [
  { key: 'draft', label: 'Drafts', icon: Pencil },
  { key: 'approved', label: 'Approved', icon: Check },
  { key: 'queued', label: 'Queued', icon: Zap },
  { key: 'sent', label: 'Sent', icon: Send },
  { key: 'replied', label: 'Replied', icon: Inbox },
  { key: 'bounced', label: 'Bounced', icon: AlertTriangle },
  { key: 'skipped', label: 'Skipped', icon: AlertTriangle }
];

const emptyCand = { name: '', firm: '', office: '', title: '', email: '' };

// Human "in ~2h 10m" / "now" for the next scheduled background pass.
function fmtNext(iso) {
  if (!iso) return 'soon';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 60_000) return 'now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// The 4-stage outreach workflow, derived from the pipeline counts.
const STEPS = [
  { key: 'add', label: 'Add candidates' },
  { key: 'draft', label: 'Generate drafts' },
  { key: 'review', label: 'Review & approve' },
  { key: 'send', label: 'Send or schedule' }
];

export default function OutreachPanel() {
  const { user } = UserAuth() || {};
  const navigate = useNavigate();

  const [recruiter, setRecruiter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('outreachRecruiter')) || { name: '', org: '', address: '' }; }
    catch { return { name: '', org: '', address: '' }; }
  });
  const [roleTitle, setRoleTitle] = useState('');
  const [roleQuery, setRoleQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [newCand, setNewCand] = useState(emptyCand);
  const [bulk, setBulk] = useState('');

  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [stats, setStats] = useState({ byStatus: {}, quota: { used: 0, cap: 0, remaining: 0 } });
  const [edits, setEdits] = useState({}); // id -> {subject, body, email}
  const [gmailConnected, setGmailConnected] = useState(isGmailConnected());
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [suppression, setSuppression] = useState([]);
  const [suppressEmail, setSuppressEmail] = useState('');
  const [bg, setBg] = useState(null); // background-sync status/settings
  const [oauthForm, setOauthForm] = useState({ clientId: '', clientSecret: '' });
  const [showOAuthForm, setShowOAuthForm] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const persistRecruiter = (r) => {
    setRecruiter(r);
    try { localStorage.setItem('outreachRecruiter', JSON.stringify(r)); } catch (e) { /* ignore */ }
  };

  const flash = (text, kind = 'info') => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 5000); };

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      const [recs, st, supp] = await Promise.all([listOutreach(statusFilter), getStats(), listSuppression()]);
      setRecords(recs || []);
      setStats(st || { byStatus: {}, quota: {} });
      setSuppression(supp || []);
    } catch (e) { flash(e.message, 'error'); }
  }, [user, statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  // --- Background sync (server-side OAuth + scheduler) ---
  const loadBg = useCallback(async () => {
    if (!user) return;
    try { setBg(await backgroundStatus()); } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => { loadBg(); }, [loadBg]);

  // Handle the OAuth redirect back (?gmail=connected|error).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('gmail');
    if (g) {
      if (g === 'connected') flash('Background Gmail connected — the scheduler can now send and sync while you are offline.', 'success');
      else flash(`Background Gmail connect failed: ${params.get('reason') || g}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
      loadBg();
    }
  }, [loadBg]);

  const saveOAuthConfig = async () => {
    if (!oauthForm.clientId.trim()) return flash('Enter your Google OAuth client ID.', 'error');
    if (!oauthForm.clientSecret.trim() && !bg?.clientId) return flash('Enter your Google OAuth client secret.', 'error');
    setBusy('bgoauth');
    try {
      await saveBackgroundOAuthConfig(oauthForm);
      setOauthForm({ clientId: '', clientSecret: '' });
      setShowOAuthForm(false);
      await loadBg();
      flash('Google OAuth client saved. Now click "Connect background Gmail".', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const connectBackground = async () => {
    setBusy('bgconnect');
    try {
      // Persist sender identity first so the scheduler has it for the footer.
      await updateBackgroundSettings({ sender: recruiter }).catch(() => {});
      const { url } = await backgroundAuthUrl();
      window.location.href = url; // full redirect to Google consent
    } catch (e) { flash(e.message, 'error'); setBusy(''); }
  };

  const toggleBg = async (patch) => {
    setBusy('bgsettings');
    try { await updateBackgroundSettings({ ...patch, sender: recruiter }); await loadBg(); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const disconnectBg = async () => {
    if (!window.confirm('Disconnect background Gmail? Scheduled sends and auto-sync will stop.')) return;
    setBusy('bgdisc');
    try { await disconnectBackground(); await loadBg(); flash('Background Gmail disconnected.', 'success'); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const runBgNow = async () => {
    setBusy('bgrun');
    try { await runBackgroundNow(); await Promise.all([loadBg(), reload()]); flash('Background pass complete — replies synced, queue and follow-ups processed.', 'success'); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const queueAllApproved = async () => {
    if (!recruiter.address?.trim()) return flash('Add your mailing address (Sender details) first.', 'error');
    setBusy('queue');
    try {
      const r = await queueApproved({ recruiter, spacingMinutes: 3 });
      await reload();
      flash(`Queued ${r.queued} for background send (the scheduler will send them paced).`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  // Export the full outreach pipeline to CSV (client-side, all statuses).
  const exportCsv = async () => {
    setBusy('export');
    try {
      const all = await listOutreach(); // no filter = every status
      const cols = ['name', 'firm', 'office', 'email', 'status', 'subject', 'replyClassification', 'emailWarning', 'updatedAt'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
      const rows = (all || []).map((r) => [
        r.candidate?.name, r.candidate?.firm, r.candidate?.office, r.candidate?.email,
        r.status, r.subject, r.reply?.classification || '', r.emailWarning || '', r.updatedAt
      ].map(esc).join(','));
      const csv = [cols.join(','), ...rows].join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `outreach-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      flash(`Exported ${all?.length || 0} records.`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const addSuppress = async () => {
    if (!suppressEmail.trim()) return;
    setBusy('suppress');
    try { await addSuppression(suppressEmail.trim()); setSuppressEmail(''); await reload(); flash('Added to do-not-contact.', 'success'); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };
  const removeSuppress = async (email) => {
    try { await removeSuppression(email); await reload(); }
    catch (e) { flash(e.message, 'error'); }
  };

  const pullShortlist = useCallback(async (query) => {
    const q = (query ?? roleQuery).trim();
    if (!q) return flash('Add the role/search context first.', 'error');
    setBusy('shortlist');
    try {
      const { candidates: pulled, withEmail } = await fetchShortlist(q, 12);
      if (!pulled?.length) {
        flash('No candidates with emails matched this search.', 'error');
      } else {
        setCandidates(pulled.map((c) => ({ ...emptyCand, ...c })));
        flash(`Pulled ${withEmail} candidate(s) with emails from your search.`, 'success');
      }
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  }, [roleQuery]);

  // Handoff from the search page: prefill the role query and auto-pull the shortlist.
  useEffect(() => {
    // Explicit candidate seed (if a caller staged candidates directly).
    try {
      const seed = JSON.parse(localStorage.getItem('outreachShortlist'));
      if (Array.isArray(seed) && seed.length) {
        setCandidates(seed.map((c) => ({ ...emptyCand, ...c })));
        localStorage.removeItem('outreachShortlist');
      }
    } catch (e) { /* ignore */ }
    // Role-query handoff (set by the landing page after a search).
    try {
      const q = localStorage.getItem('outreachRoleQuery');
      if (q && q.trim()) {
        setRoleQuery(q);
        localStorage.removeItem('outreachRoleQuery');
        pullShortlist(q);
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <div style={s.gate}>Please sign in to use outreach.</div>;
  }

  const addCandidate = () => {
    if (!newCand.name.trim()) return;
    setCandidates((prev) => [...prev, newCand]);
    setNewCand(emptyCand);
  };
  const parseBulk = () => {
    const rows = bulk.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
      const [name, firm, email, office] = line.split(/\s*,\s*/);
      return { ...emptyCand, name: name || '', firm: firm || '', email: email || '', office: office || '' };
    }).filter((c) => c.name);
    setCandidates((prev) => [...prev, ...rows]);
    setBulk('');
  };

  const generate = async () => {
    if (!candidates.length) return flash('Add at least one candidate.', 'error');
    if (!roleQuery.trim()) return flash('Add the role/search context first.', 'error');
    setBusy('generate');
    try {
      await draftOutreach({ candidates, roleQuery, roleTitle, recruiter });
      setCandidates([]);
      setStatusFilter('draft');
      await reload();
      flash('Drafts generated.', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const editVal = (rec, field) => (edits[rec._id]?.[field] ?? rec[field] ?? rec.candidate?.[field] ?? '');
  const setEdit = (id, field, value) => setEdits((p) => ({ ...p, [id]: { ...p[id], [field]: value } }));

  const saveEdits = async (rec) => {
    const e = edits[rec._id] || {};
    setBusy(rec._id);
    try {
      const patch = {};
      if (e.subject !== undefined) patch.subject = e.subject;
      if (e.body !== undefined) patch.body = e.body;
      if (e.email !== undefined) patch.email = e.email;
      await updateOutreach(rec._id, patch);
      setEdits((p) => { const n = { ...p }; delete n[rec._id]; return n; });
      await reload();
    } catch (err) { flash(err.message, 'error'); }
    setBusy('');
  };

  const approve = async (rec) => {
    const e = edits[rec._id] || {};
    setBusy(rec._id);
    try {
      await updateOutreach(rec._id, { subject: editVal(rec, 'subject'), body: editVal(rec, 'body'), email: e.email ?? rec.candidate?.email, approve: true });
      setEdits((p) => { const n = { ...p }; delete n[rec._id]; return n; });
      await reload();
      flash('Approved.', 'success');
    } catch (err) { flash(err.message, 'error'); }
    setBusy('');
  };

  const connect = async () => {
    setBusy('gmail');
    try { await connectGmail(); setGmailConnected(true); flash('Gmail connected.', 'success'); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const guardSend = () => {
    if (!recruiter.address?.trim()) {
      flash('Add your business mailing address (required for CAN-SPAM) in Sender details before sending.', 'error');
      return false;
    }
    return true;
  };

  const doGmailDraft = async (rec) => {
    setBusy(rec._id);
    try { const token = await ensureGmailToken(); setGmailConnected(true); await createGmailDraft(rec._id, token, recruiter); await reload(); flash('Gmail draft created.', 'success'); }
    catch (e) { if (/token/i.test(e.message)) clearGmailToken(); flash(e.message, 'error'); }
    setBusy('');
  };

  const doSend = async (rec) => {
    if (!guardSend()) return;
    if (!window.confirm(`Send this email to ${rec.candidate?.email}? This goes to a real person.`)) return;
    setBusy(rec._id);
    try { const token = await ensureGmailToken(); setGmailConnected(true); await sendOutreach(rec._id, token, recruiter); await reload(); flash('Sent.', 'success'); }
    catch (e) { if (/token/i.test(e.message)) clearGmailToken(); flash(e.message, 'error'); }
    setBusy('');
  };

  const doQueue = async (rec) => {
    if (!guardSend()) return;
    if (!bg?.connected) return flash('Connect background Gmail before queueing.', 'error');
    setBusy(rec._id);
    try {
      await queueOutreach(rec._id, { recruiter });
      await reload();
      flash('Queued for background send.', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  // Paced batch send: best-practice deliverability — never burst cold outreach. Sends
  // approved emails sequentially ~10s apart (just over the server's 8s min interval),
  // stops on quota/pace limits, and respects the per-record send gate.
  const sendAllApproved = async () => {
    if (!guardSend()) return;
    const queue = records.filter((r) => r.status === 'approved' || r.status === 'gmail_draft');
    if (!queue.length) return flash('No approved emails to send.', 'error');
    if (!window.confirm(`Send ${queue.length} approved email(s) to real people, paced ~10s apart? You can stop by leaving the page.`)) return;
    setBusy('sendall');
    let sent = 0;
    try {
      const token = await ensureGmailToken();
      setGmailConnected(true);
      for (let i = 0; i < queue.length; i++) {
        try {
          await sendOutreach(queue[i]._id, token, recruiter);
          sent += 1;
          flash(`Sending… ${sent}/${queue.length}`, 'info');
        } catch (e) {
          if (/cap reached|pace your sends|token/i.test(e.message)) { if (/token/i.test(e.message)) clearGmailToken(); flash(e.message, 'error'); break; }
          // otherwise skip this one and continue
        }
        if (i < queue.length - 1) await new Promise((r) => setTimeout(r, 9500));
      }
      await reload();
      flash(`Sent ${sent} of ${queue.length}.`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const doFollowUp = async (rec) => {
    if (!guardSend()) return;
    setBusy(rec._id);
    try { const token = await ensureGmailToken(); await followUp(rec._id, { googleAccessToken: token, send: true, recruiter }); await reload(); flash('Follow-up sent.', 'success'); }
    catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const doSync = async () => {
    setBusy('sync');
    try { const token = await ensureGmailToken(); setGmailConnected(true); const r = await syncReplies(token); await reload(); flash(`Checked ${r.checked}, ${r.updated} new replies.`, 'success'); }
    catch (e) { if (/token/i.test(e.message)) clearGmailToken(); flash(e.message, 'error'); }
    setBusy('');
  };

  // Derive the workflow stage from the pipeline counts (drives the stepper).
  const cnt = stats.byStatus || {};
  const draftN = cnt.draft || 0;
  const approvedN = (cnt.approved || 0) + (cnt.gmail_draft || 0) + (cnt.queued || 0);
  const sentN = (cnt.sent || 0) + (cnt.replied || 0) + (cnt.bounced || 0);
  const stepDone = {
    add: candidates.length > 0 || draftN > 0 || approvedN > 0 || sentN > 0,
    draft: draftN > 0 || approvedN > 0 || sentN > 0,
    review: approvedN > 0 || sentN > 0,
    send: sentN > 0
  };
  const activeStep = STEPS.findIndex((st) => !stepDone[st.key]);
  const onStepClick = (key) => {
    if (key === 'review') setStatusFilter('draft');
    else if (key === 'send') setStatusFilter('approved');
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <button style={s.backBtn} onClick={() => navigate('/')}><ArrowLeft size={16} /> Back to search</button>
        <h2 style={s.h2}><Mail size={20} /> Outreach Agents</h2>
        <div style={s.gmailBox}>
          <span style={{ ...s.dot, background: gmailConnected ? '#2e9e5b' : '#c0392b' }} />
          {gmailConnected ? 'Gmail connected' : 'Gmail not connected'}
          <button style={s.smallBtn} onClick={connect} disabled={busy === 'gmail'}>{gmailConnected ? 'Reconnect' : 'Connect Gmail'}</button>
        </div>
      </div>

      {msg && <div style={{ ...s.flash, ...(msg.kind === 'error' ? s.flashErr : msg.kind === 'success' ? s.flashOk : {}) }}>{msg.text}</div>}

      {/* Guided workflow stepper */}
      <div style={s.stepper}>
        {STEPS.map((st, i) => {
          const done = stepDone[st.key];
          const active = i === activeStep;
          return (
            <React.Fragment key={st.key}>
              <button style={s.step} onClick={() => onStepClick(st.key)}>
                <span style={{ ...s.stepNum, ...(done ? s.stepNumDone : active ? s.stepNumActive : {}) }}>
                  {done ? <Check size={13} /> : i + 1}
                </span>
                <span style={{ ...s.stepLabel, ...(active ? { color: '#1f1f1c', fontWeight: 700 } : {}) }}>{st.label}</span>
              </button>
              {i < STEPS.length - 1 && <span style={{ ...s.stepBar, ...(done ? { background: '#2e9e5b' } : {}) }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div style={s.quotaRow}>
        <span>Daily send quota: <b>{stats.quota?.remaining ?? 0}</b> of {stats.quota?.cap ?? 0} left</span>
        {bg?.connected && (
          <span style={s.bgChip} title={`Auto-runs every ${bg.settings?.syncIntervalHours || 1}h`}>
            <Zap size={12} /> Background on · next ~{fmtNext(bg.nextSyncAt)}
          </span>
        )}
        {STATUS_TABS.map((t) => <span key={t.key} style={s.statChip}>{t.label}: <b>{stats.byStatus?.[t.key] || 0}</b></span>)}
        <button style={s.smallBtn} onClick={doSync} disabled={busy === 'sync'}><RefreshCw size={13} /> Sync replies</button>
        <button style={s.smallBtn} onClick={exportCsv} disabled={busy === 'export'}><Download size={13} /> Export CSV</button>
      </div>

      {/* Analytics */}
      {(stats.rates?.totalSent > 0) && (
        <div style={s.analytics}>
          <span style={s.metric}><b>{stats.rates.totalSent}</b> sent</span>
          <span style={s.metric}>Reply rate <b>{stats.rates.replyRate}%</b></span>
          <span style={s.metric}>Interested <b style={{ color: '#2e9e5b' }}>{stats.rates.interestedRate}%</b></span>
          <span style={s.metric}>Bounce <b style={{ color: stats.rates.bounceRate > 5 ? '#c0392b' : '#6b6b63' }}>{stats.rates.bounceRate}%</b></span>
          {stats.suppressionCount > 0 && <span style={s.metric}>Do-not-contact <b>{stats.suppressionCount}</b></span>}
        </div>
      )}

      {/* Setup & settings — all config grouped so the main workflow stays front-and-centre */}
      <div style={s.card}>
        <button style={s.setupHeader} onClick={() => setSetupOpen((o) => !o)}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>⚙ Setup &amp; settings</span>
          <span style={s.muted}>
            {recruiter.address?.trim() ? 'Sender ✓' : 'Sender needed'} · Background {bg?.connected ? 'on' : 'off'} · Do-not-contact {suppression.length} &nbsp;{setupOpen ? '▾' : '▸'}
          </span>
        </button>
        {setupOpen && (<>

      {/* Sender details (footer / CAN-SPAM) */}
      <details style={s.subcard}>
        <summary style={s.summary}>Sender details (used in the required email footer)</summary>
        <div style={s.formRow}>
          <input style={s.in} placeholder="Your name" value={recruiter.name} onChange={(e) => persistRecruiter({ ...recruiter, name: e.target.value })} />
          <input style={s.in} placeholder="Company / firm" value={recruiter.org} onChange={(e) => persistRecruiter({ ...recruiter, org: e.target.value })} />
          <input style={{ ...s.in, flex: 2 }} placeholder="Business mailing address (required to send)" value={recruiter.address} onChange={(e) => persistRecruiter({ ...recruiter, address: e.target.value })} />
        </div>
        {!recruiter.address?.trim() && <div style={s.warn}><ShieldAlert size={14} /> A physical mailing address is legally required (CAN-SPAM) before you can send.</div>}
      </details>

      {/* Background sync (server-side OAuth + scheduler) */}
      <details style={s.subcard}>
        <summary style={s.summary}>
          <Zap size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Background sync {bg?.connected ? '· connected' : bg?.hasOAuthClient ? '· ready to connect' : '· setup'}
        </summary>
        <div style={{ fontSize: 12.5, color: '#6b6b63', margin: '8px 0' }}>
          Lets the server send queued emails, sync/triage replies, and fire gated follow-ups <b>while you are offline</b>, using your own Google app + a one-time authorization.
        </div>

        {/* Step 1 — the user provides their OWN Google OAuth client. */}
        {bg && (!bg.hasOAuthClient || showOAuthForm) && (
          <div style={s.oauthBox}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Step 1 · Your Google OAuth client</div>
            <ol style={s.oauthSteps}>
              <li>In <b>Google Cloud Console</b> → enable the <b>Gmail API</b>.</li>
              <li>Create an <b>OAuth 2.0 Web client</b> and copy its ID + secret.</li>
              <li>Add this exact <b>Authorized redirect URI</b> to that client:
                <code style={s.code}>{bg.redirectUri}</code>
              </li>
            </ol>

            <details style={s.helpBox}>
              <summary style={s.helpSummary}>How do I create a Google OAuth client? (~3 min)</summary>
              <ol style={s.helpSteps}>
                <li>Open the <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noopener noreferrer" style={s.link}>Google Cloud Console</a> and create (or pick) a project.</li>
                <li>Enable Gmail: go to the <a href="https://console.cloud.google.com/apis/library/gmail.googleapis.com" target="_blank" rel="noopener noreferrer" style={s.link}>Gmail API page</a> → <b>Enable</b>.</li>
                <li>Configure the <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer" style={s.link}>OAuth consent screen</a> → <b>External</b> → fill app name + your email. While it's in <i>Testing</i>, add your Gmail under <b>Test users</b> (no Google verification needed for your own account).</li>
                <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" style={s.link}>Credentials</a> → <b>Create credentials</b> → <b>OAuth client ID</b> → application type <b>Web application</b>.</li>
                <li>Under <b>Authorized redirect URIs</b>, click <b>Add URI</b> and paste exactly:
                  <code style={s.code}>{bg.redirectUri}</code>
                </li>
                <li><b>Create</b>, then copy the <b>Client ID</b> and <b>Client secret</b> into the fields below.</li>
              </ol>
              <div style={s.muted}>The redirect URI must match character-for-character, or Google returns <i>redirect_uri_mismatch</i>.</div>
            </details>

            <input style={s.in} placeholder="Client ID (…apps.googleusercontent.com)" value={oauthForm.clientId}
              onChange={(e) => setOauthForm({ ...oauthForm, clientId: e.target.value })} />
            <input style={{ ...s.in, marginTop: 6 }} type="password" placeholder={bg.clientId ? 'Client secret (leave blank to keep current)' : 'Client secret (GOCSPX-…)'}
              value={oauthForm.clientSecret} onChange={(e) => setOauthForm({ ...oauthForm, clientSecret: e.target.value })} />
            <div style={{ ...s.actions, marginTop: 8 }}>
              <button style={s.primaryBtn} onClick={saveOAuthConfig} disabled={busy === 'bgoauth'}>
                {busy === 'bgoauth' ? 'Saving…' : 'Save Google client'}
              </button>
              {bg.hasOAuthClient && <button style={s.smallBtn} onClick={() => { setShowOAuthForm(false); setOauthForm({ clientId: '', clientSecret: '' }); }}>Cancel</button>}
            </div>
            <div style={s.muted}>Your client secret is stored encrypted on the server and never shown again.</div>
          </div>
        )}

        {/* Step 2 — authorize with the saved client. */}
        {bg && bg.hasOAuthClient && !showOAuthForm && !bg.connected && (
          <div>
            <button style={s.primaryBtn} onClick={connectBackground} disabled={busy === 'bgconnect'}>
              <Zap size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Connect background Gmail
            </button>
            <button style={{ ...s.smallBtn, marginLeft: 8 }} onClick={() => setShowOAuthForm(true)}>Edit Google client</button>
          </div>
        )}
        {bg && bg.connected && (
          <div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              Connected as <b>{bg.email}</b>
              {bg.lastSyncAt ? <span style={s.muted}> · last sync {new Date(bg.lastSyncAt).toLocaleString()}</span> : <span style={s.muted}> · not run yet</span>}
            </div>

            {/* How often the server runs in the background */}
            <label style={s.bgToggle}>
              Run automatically every
              <select value={bg.settings?.syncIntervalHours || 1} onChange={(e) => toggleBg({ syncIntervalHours: Number(e.target.value) })} disabled={busy === 'bgsettings'} style={s.intervalSelect}>
                {[1, 2, 3, 4, 6, 8, 12, 24].map((h) => <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>)}
              </select>
              <span style={s.muted}>· next ~{fmtNext(bg.nextSyncAt)}</span>
            </label>

            <label style={s.bgToggle}><input type="checkbox" checked={!!bg.settings?.autoSyncReplies} onChange={(e) => toggleBg({ autoSyncReplies: e.target.checked })} disabled={busy === 'bgsettings'} /> Auto-sync &amp; triage replies (recommended)</label>
            <label style={s.bgToggle}><input type="checkbox" checked={!!bg.settings?.autoSendQueue} onChange={(e) => toggleBg({ autoSendQueue: e.target.checked })} disabled={busy === 'bgsettings'} /> Auto-send queued emails (paced, capped)</label>
            <label style={s.bgToggle}>
              <input type="checkbox" checked={!!bg.settings?.autoFollowUp} onChange={(e) => toggleBg({ autoFollowUp: e.target.checked })} disabled={busy === 'bgsettings'} /> Auto follow-up after
              <input type="number" min="1" max="30" value={bg.settings?.followUpDays || 4} onChange={(e) => toggleBg({ followUpDays: Number(e.target.value) })} style={s.daysInput} /> days of no reply
            </label>
            <div style={s.actions}>
              <button style={s.primaryBtnSm} onClick={runBgNow} disabled={busy === 'bgrun'}>
                <RefreshCw size={13} /> {busy === 'bgrun' ? 'Running…' : 'Run sync now'}
              </button>
              <button style={s.smallBtn} onClick={connectBackground}>Reconnect</button>
              <button style={s.smallBtn} onClick={() => setShowOAuthForm(true)}>Edit Google client</button>
              <button style={s.smallBtn} onClick={disconnectBg} disabled={busy === 'bgdisc'}>Disconnect</button>
            </div>
          </div>
        )}
      </details>

      {/* Do-not-contact / suppression list */}
      <details style={s.subcard}>
        <summary style={s.summary}><Ban size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Do-not-contact list ({suppression.length})</summary>
        <div style={{ fontSize: 12.5, color: '#6b6b63', margin: '8px 0' }}>
          Recipients here are never drafted or sent to. Opt-out replies ("unsubscribe", "not interested") and bounces are added here automatically.
        </div>
        <div style={s.formRow}>
          <input style={s.in} placeholder="Add an email to suppress" value={suppressEmail} onChange={(e) => setSuppressEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSuppress()} />
          <button style={s.smallBtn} onClick={addSuppress} disabled={busy === 'suppress'}><Ban size={13} /> Add</button>
        </div>
        {suppression.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {suppression.map((sup) => (
              <div key={sup._id || sup.email} style={s.suppRow}>
                <span>{sup.email}</span>
                <span style={s.suppReason}>{sup.reason}</span>
                <button style={s.iconBtn} title="Remove" onClick={() => removeSuppress(sup.email)}><XIcon size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </details>

        </>)}
      </div>

      {/* Compose: role + candidates */}
      <div style={s.card}>
        <div style={s.formRow}>
          <input style={s.in} placeholder="Role title (e.g. Senior Associate, Global Trade)" value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} />
        </div>
        <textarea style={s.textarea} placeholder="Paste the role / search context (the job description or search brief). Drafts are grounded in this + each candidate's profile." value={roleQuery} onChange={(e) => setRoleQuery(e.target.value)} />

        <div style={s.subheadRow}>
          <span style={s.subhead}>Candidates ({candidates.length})</span>
          <button style={s.smallBtn} onClick={() => pullShortlist()} disabled={busy === 'shortlist'}>
            <Users size={14} /> {busy === 'shortlist' ? 'Pulling…' : 'Pull shortlist from search'}
          </button>
        </div>
        <div style={s.formRow}>
          <input style={s.in} placeholder="Name" value={newCand.name} onChange={(e) => setNewCand({ ...newCand, name: e.target.value })} />
          <input style={s.in} placeholder="Firm" value={newCand.firm} onChange={(e) => setNewCand({ ...newCand, firm: e.target.value })} />
          <input style={s.in} placeholder="Office" value={newCand.office} onChange={(e) => setNewCand({ ...newCand, office: e.target.value })} />
          <input style={s.in} placeholder="Email" value={newCand.email} onChange={(e) => setNewCand({ ...newCand, email: e.target.value })} />
          <button style={s.smallBtn} onClick={addCandidate}><UserPlus size={14} /> Add</button>
        </div>
        <details style={{ marginBottom: 8 }}>
          <summary style={s.summarySm}>Bulk paste (one per line: Name, Firm, email, Office)</summary>
          <textarea style={s.textarea} placeholder={'Jane Doe, Davis Polk, jdoe@firm.com, New York\nJohn Smith, Cravath, jsmith@firm.com, New York'} value={bulk} onChange={(e) => setBulk(e.target.value)} />
          <button style={s.smallBtn} onClick={parseBulk}>Add pasted</button>
        </details>

        {candidates.length > 0 && (
          <div style={s.stagedWrap}>
            {candidates.map((c, i) => (
              <div key={i} style={{ ...s.staged, ...(c.emailWarning ? s.stagedWarn : {}) }} title={c.emailWarning || ''}>
                {c.emailWarning && <AlertTriangle size={12} color="#b9770e" />}
                <span>{c.name}{c.firm ? ` · ${c.firm}` : ''}{c.email ? '' : ' (no email)'}</span>
                <button style={s.iconBtn} onClick={() => setCandidates((p) => p.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
        <button style={s.primaryBtn} onClick={generate} disabled={busy === 'generate'}>
          {busy === 'generate' ? 'Generating…' : '✦ Generate drafts with Claude'}
        </button>
      </div>

      {/* Pipeline */}
      <div style={s.tabs}>
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatusFilter(t.key)} style={{ ...s.tab, ...(statusFilter === t.key ? s.tabActive : {}) }}>
            <t.icon size={14} /> {t.label} ({stats.byStatus?.[t.key] || 0})
          </button>
        ))}
      </div>

      {statusFilter === 'approved' && records.some((r) => r.status === 'approved' || r.status === 'gmail_draft') && (
        <div style={s.batchBar}>
          <span style={{ fontSize: 12.5, color: '#6b6b63' }}>Best practice: sends are paced and capped daily for deliverability.</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {bg?.connected && (
              <button style={s.smallBtn} onClick={queueAllApproved} disabled={busy === 'queue'} title="Hand off to the background scheduler — sends even if you close the tab">
                <Zap size={14} /> {busy === 'queue' ? 'Queuing…' : 'Queue for background send'}
              </button>
            )}
            <button style={s.sendBtn} onClick={sendAllApproved} disabled={busy === 'sendall'}>
              <Send size={14} /> {busy === 'sendall' ? 'Sending…' : 'Send all now (paced)'}
            </button>
          </div>
        </div>
      )}

      <div style={s.list}>
        {records.length === 0 && <div style={s.empty}>No {statusFilter} items.</div>}
        {records.map((rec) => {
          const editing = !!edits[rec._id];
          return (
            <div key={rec._id} style={s.record}>
              <div style={s.recHead}>
                <div>
                  <b>{rec.candidate?.name}</b>{rec.candidate?.firm ? ` · ${rec.candidate.firm}` : ''}
                  {rec.candidate?.office ? <span style={s.muted}> · {rec.candidate.office}</span> : null}
                </div>
                {rec.compliance?.requiresConsent && (
                  <span style={s.consent} title="CASL/GDPR region — ensure you have a lawful basis to contact.">
                    <ShieldAlert size={13} /> {rec.compliance.region.toUpperCase()} · consent
                  </span>
                )}
              </div>

              <div style={s.recEmail}>
                To: {rec.candidate?.email
                  ? rec.candidate.email
                  : <input style={s.inlineIn} placeholder="add recipient email" value={editVal(rec, 'email')} onChange={(e) => setEdit(rec._id, 'email', e.target.value)} />}
              </div>

              {rec.emailWarning && (
                <div style={s.emailWarn} title={rec.emailWarning}>
                  <AlertTriangle size={13} /> {rec.emailWarning}
                  {(rec.status === 'draft' || rec.status === 'skipped') && (
                    <input style={{ ...s.inlineIn, marginLeft: 6 }} placeholder="correct email" value={editVal(rec, 'email')} onChange={(e) => setEdit(rec._id, 'email', e.target.value)} />
                  )}
                </div>
              )}

              {rec.status === 'skipped' && <div style={s.warn}><AlertTriangle size={13} /> {rec.lastError || 'skipped'}</div>}
              {rec.status === 'failed' && <div style={s.warn}><AlertTriangle size={13} /> Send failed: {rec.lastError}</div>}

              {rec.preflight && rec.preflight.warnings && rec.preflight.warnings.length > 0 && (
                <div style={s.preflightBox}>
                  <div style={s.preflightHead}>
                    <ShieldAlert size={13} /> Deliverability check — personalization {rec.preflight.personalizationScore}%
                  </div>
                  {rec.preflight.warnings.map((w, i) => (
                    <div key={i} style={{ ...s.preflightWarn, ...(w.level === 'error' ? s.preflightErr : {}) }}>
                      {w.level === 'error' ? '●' : '○'} {w.message}
                    </div>
                  ))}
                </div>
              )}

              {(rec.status === 'draft' || rec.status === 'skipped') ? (
                <>
                  <input style={s.in} value={editVal(rec, 'subject')} onChange={(e) => setEdit(rec._id, 'subject', e.target.value)} placeholder="Subject" />
                  <textarea style={s.textareaTall} value={editVal(rec, 'body')} onChange={(e) => setEdit(rec._id, 'body', e.target.value)} />
                  <div style={s.actions}>
                    {editing && <button style={s.smallBtn} onClick={() => saveEdits(rec)} disabled={busy === rec._id}>Save</button>}
                    <button style={s.approveBtn} onClick={() => approve(rec)} disabled={busy === rec._id}><Check size={14} /> Approve</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={s.subjectLine}>{rec.subject}</div>
                  <pre style={s.bodyPre}>{rec.body}</pre>
                </>
              )}

              {rec.status === 'approved' && (
                <div style={s.actions}>
                  <button style={s.smallBtn} onClick={() => doGmailDraft(rec)} disabled={busy === rec._id}>Create Gmail draft</button>
                  {bg?.connected && <button style={s.smallBtn} onClick={() => doQueue(rec)} disabled={busy === rec._id}><Zap size={14} /> Queue</button>}
                  <button style={s.sendBtn} onClick={() => doSend(rec)} disabled={busy === rec._id}><Send size={14} /> Send</button>
                </div>
              )}
              {rec.status === 'gmail_draft' && (
                <div style={s.actions}>
                  <span style={s.muted}>Gmail draft created.</span>
                  {bg?.connected && <button style={s.smallBtn} onClick={() => doQueue(rec)} disabled={busy === rec._id}><Zap size={14} /> Queue</button>}
                  <button style={s.sendBtn} onClick={() => doSend(rec)} disabled={busy === rec._id}><Send size={14} /> Send</button>
                </div>
              )}
              {rec.status === 'queued' && (
                <div style={s.actions}>
                  <span style={s.sentTag}><Zap size={13} /> Queued for background send{rec.scheduledFor ? ` · ${new Date(rec.scheduledFor).toLocaleString()}` : ''}</span>
                </div>
              )}
              {rec.status === 'sent' && (
                <div style={s.actions}>
                  <span style={s.sentTag}><Check size={13} /> Sent{rec.followUps?.length ? ` · ${rec.followUps.length} follow-up(s)` : ''}</span>
                  <button style={s.smallBtn} onClick={() => doFollowUp(rec)} disabled={busy === rec._id || (rec.followUps?.length || 0) >= 2}><Clock size={13} /> Follow up</button>
                </div>
              )}
              {rec.status === 'replied' && (
                <div style={s.replyBox}>
                  <b style={{ textTransform: 'capitalize' }}>{(rec.reply?.classification || 'reply').replace('_', ' ')}</b>
                  {rec.reply?.summary ? ` — ${rec.reply.summary}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  page: { maxWidth: 920, margin: '0 auto', padding: '20px 18px 60px', fontFamily: '"Roboto","Helvetica","Arial",sans-serif', color: '#2c2c28' },
  gate: { padding: 40, textAlign: 'center', color: '#6b6b63' },
  topbar: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid #e0e0da', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#5d5d57', fontSize: 13 },
  h2: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, margin: 0, flex: 1 },
  gmailBox: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5d5d57' },
  dot: { width: 9, height: 9, borderRadius: '50%' },
  quotaRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 13, color: '#5d5d57', margin: '14px 0' },
  statChip: { color: '#6b6b63' },
  analytics: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#5d5d57', background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 10, padding: '10px 14px', margin: '0 0 10px' },
  metric: { color: '#6b6b63' },
  suppRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '5px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 7 },
  suppReason: { marginLeft: 'auto', fontSize: 11.5, color: '#b9770e', background: '#fdf3e3', borderRadius: 5, padding: '1px 7px' },
  card: { background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 12, padding: 14, margin: '12px 0' },
  summary: { cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#3d3d3a' },
  summarySm: { cursor: 'pointer', fontSize: 12.5, color: '#6b6b63' },
  formRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
  subhead: { fontWeight: 700, fontSize: 13, color: '#3d3d3a' },
  subheadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '14px 0 2px', flexWrap: 'wrap' },
  in: { flex: 1, minWidth: 120, padding: '8px 10px', border: '1px solid #dcdcd4', borderRadius: 8, fontSize: 13.5, outline: 'none' },
  inlineIn: { padding: '4px 7px', border: '1px solid #dcdcd4', borderRadius: 6, fontSize: 12.5, width: 220 },
  textarea: { width: '100%', minHeight: 64, marginTop: 8, padding: '9px 11px', border: '1px solid #dcdcd4', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  textareaTall: { width: '100%', minHeight: 150, marginTop: 8, padding: '9px 11px', border: '1px solid #dcdcd4', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 },
  stagedWrap: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0' },
  staged: { display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e6e6e1', borderRadius: 16, padding: '4px 6px 4px 12px', fontSize: 12.5 },
  stagedWarn: { background: '#fdf3e3', border: '1px solid #f0d9af' },
  emailWarn: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, color: '#b9770e', background: '#fdf3e3', border: '1px solid #f0d9af', borderRadius: 8, padding: '6px 9px', fontSize: 12.5, margin: '0 0 8px' },
  primaryBtn: { marginTop: 12, background: '#d97757', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 18, borderBottom: '1px solid #ececE6', paddingBottom: 8 },
  tab: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid #e6e6e1', borderRadius: 8, padding: '6px 11px', cursor: 'pointer', fontSize: 13, color: '#5d5d57' },
  tabActive: { background: '#3d3d3a', color: '#fff', borderColor: '#3d3d3a' },
  list: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 },
  empty: { color: '#9a9a90', fontSize: 14, padding: 18, textAlign: 'center' },
  record: { border: '1px solid #e6e6e1', borderRadius: 12, padding: 14, background: '#fff' },
  recHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 14 },
  recEmail: { fontSize: 12.5, color: '#6b6b63', margin: '4px 0 8px' },
  muted: { color: '#9a9a90' },
  consent: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: '#b9770e', background: '#fdf3e3', border: '1px solid #f0d9af', borderRadius: 6, padding: '2px 7px' },
  subjectLine: { fontWeight: 600, fontSize: 13.5, marginBottom: 6 },
  bodyPre: { whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, color: '#3d3d3a', margin: 0, background: '#fbfbfa', border: '1px solid #f0f0ea', borderRadius: 8, padding: 10 },
  preflightBox: { margin: '8px 0', padding: '8px 10px', background: '#fbfaf6', border: '1px solid #ece7d8', borderRadius: 8 },
  preflightHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#7a6a3a', marginBottom: 4 },
  preflightWarn: { fontSize: 12, color: '#8a7a4a', lineHeight: 1.5 },
  preflightErr: { color: '#b03a2e', fontWeight: 600 },
  batchBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '12px 0 4px', padding: '10px 12px', background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 10 },
  bgToggle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#3d3d3a', margin: '6px 0', flexWrap: 'wrap' },
  daysInput: { width: 48, padding: '3px 6px', border: '1px solid #dcdcd4', borderRadius: 6, fontSize: 13, margin: '0 2px' },
  intervalSelect: { padding: '4px 8px', border: '1px solid #dcdcd4', borderRadius: 7, fontSize: 13, margin: '0 4px', background: '#fff', cursor: 'pointer' },
  primaryBtnSm: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: 'none', background: '#c2613f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  bgChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: '#eef6f0', color: '#2e7d4f', fontSize: 12, fontWeight: 600, border: '1px solid #d4e9dc' },
  // Workflow stepper
  stepper: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '4px 0 14px', padding: '12px 14px', background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 12 },
  step: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' },
  stepNum: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#e8e7e1', color: '#8a8a82', fontSize: 12, fontWeight: 700, flexShrink: 0 },
  stepNumActive: { background: '#c2613f', color: '#fff' },
  stepNumDone: { background: '#2e9e5b', color: '#fff' },
  stepLabel: { fontSize: 13, color: '#6b6b63', fontWeight: 600, whiteSpace: 'nowrap' },
  stepBar: { flex: 1, minWidth: 16, height: 2, background: '#e3e1d8', borderRadius: 2 },
  setupHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#3d3d3a' },
  subcard: { borderTop: '1px solid #ecebe6', padding: '12px 0 4px', marginTop: 10 },
  oauthBox: { background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 10, padding: 12, margin: '8px 0' },
  oauthSteps: { margin: '0 0 10px', paddingLeft: 18, fontSize: 12.5, color: '#52524b', lineHeight: 1.6 },
  code: { display: 'block', marginTop: 4, padding: '4px 8px', background: '#f0f0ea', border: '1px solid #e2e2da', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#2b2b27' },
  helpBox: { background: '#fff', border: '1px solid #ececE6', borderRadius: 8, padding: '8px 10px', margin: '0 0 10px' },
  helpSummary: { cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#3a6df0' },
  helpSteps: { margin: '8px 0 6px', paddingLeft: 18, fontSize: 12.5, color: '#52524b', lineHeight: 1.65 },
  link: { color: '#3a6df0', textDecoration: 'underline' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' },
  smallBtn: { display: 'flex', alignItems: 'center', gap: 5, background: '#fff', border: '1px solid #d8d8d0', borderRadius: 8, padding: '6px 11px', cursor: 'pointer', fontSize: 12.5, color: '#3d3d3a' },
  approveBtn: { display: 'flex', alignItems: 'center', gap: 5, background: '#2e9e5b', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 13px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  sendBtn: { display: 'flex', alignItems: 'center', gap: 5, background: '#d97757', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 13px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  sentTag: { display: 'flex', alignItems: 'center', gap: 5, color: '#2e9e5b', fontSize: 13, fontWeight: 600 },
  replyBox: { marginTop: 8, background: '#f1f7f3', border: '1px solid #cfe6d8', borderRadius: 8, padding: 10, fontSize: 13 },
  warn: { display: 'flex', alignItems: 'center', gap: 6, color: '#b9770e', fontSize: 12.5, marginTop: 8 },
  flash: { padding: '9px 13px', borderRadius: 8, fontSize: 13, margin: '8px 0', background: '#eef1f6' },
  flashErr: { background: '#fdecea', color: '#b03a2e', border: '1px solid #f3c6bf' },
  flashOk: { background: '#eaf7ef', color: '#1e7a44', border: '1px solid #c0e6cf' },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#9a9a90', padding: 2 }
};
